import { formatPriceTaxMode } from '#/modules/crmMgmt/helper/formatPriceTaxMode';
import {
  formatDateOrDash,
  formatDateTimeOrDash,
} from '#/utils/date';
import { formatDocumentCurrencyLabel } from '#/utils/documentCurrencyLabel';
import { apiDiscountRateToMultiplier } from '#/utils/discountRate';
import { formatDisplayAmount } from '#/utils/formatNumber';

export function displayOrDash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

export function formatCustomerLabel(customer) {
  if (!customer) {
    return '—';
  }
  const label = [customer.code, customer.name].filter(Boolean).join(' - ');
  return label || '—';
}

/**
 * SO Detail 客戶快照：優先 `customerSnapshot`，缺值 fallback live `customer`。
 */
export function mapSalesOrderCustomerSnapshotFields(detail) {
  if (!detail) {
    return null;
  }

  const snapshot = detail.customerSnapshot;
  const customer = detail.customer;

  return {
    customerCode: snapshot?.customerCode ?? customer?.code ?? null,
    customerName: snapshot?.customerName ?? customer?.name ?? null,
    invoiceTitle: snapshot?.invoiceTitle ?? null,
    taxIdNumber: snapshot?.taxIdNumber ?? null,
    taxCountryCode: null,
  };
}

/**
 * SO Detail 出貨 → formatAddressBlock／CrmAddressCard 形狀。
 * 取 `customerSnapshot.shippingAddress`＋`shippingContact`（可 null）。
 */
export function mapSalesOrderShippingAddress(detail) {
  if (!detail) {
    return null;
  }

  const snapshot = detail.customerSnapshot;
  const address = snapshot?.shippingAddress;
  const contact = snapshot?.shippingContact;

  if (!address && !contact) {
    return null;
  }

  return {
    postalCode: address?.postalCode ?? null,
    address: address?.address ?? null,
    addressType: address?.addressType ?? null,
    isDefault: address?.isDefault ?? null,
    recipientName: contact?.name ?? null,
    recipientPhone: contact?.mobile || contact?.phone || null,
    recipientEmail: contact?.email ?? null,
  };
}

/**
 * 銷貨單詳情主檔／金額顯示用 meta。
 * Header 金額只讀 `amountSummary`（Search 無則 hasAmount=false）。
 */
export function mapSalesOrderMeta(detail) {
  const amountSummary = detail?.amountSummary ?? null;
  const hasAmount = Boolean(amountSummary)
    && (
      amountSummary.subtotalAmount != null
      || amountSummary.discountAmount != null
      || amountSummary.netAmount != null
      || amountSummary.taxAmount != null
      || amountSummary.roundingAdjustment != null
      || amountSummary.totalAmount != null
    );

  return {
    code: detail?.salesOrderNo || '—',
    status: detail?.status || '',
    customerSubtitle: formatCustomerLabel(detail?.customer),
    plannedShipDate: formatDateOrDash(detail?.plannedShipDate),
    customerOrderId: detail?.customerOrderId ?? '',
    customerOrderNo: displayOrDash(detail?.customerOrderNo),
    stockIssueId: detail?.stockIssueId || '',
    issueReason: detail?.issueReason ?? '',
    remark: detail?.remark ?? '',
    createDate: formatDateTimeOrDash(detail?.record?.createdAt),
    reviseDate: formatDateTimeOrDash(detail?.record?.updatedAt),
    currencyCode: formatDocumentCurrencyLabel(detail),
    priceTaxMode: formatPriceTaxMode(detail?.taxMode ?? detail?.priceTaxMode),
    hasAmount,
    subtotalAmount: formatDisplayAmount(amountSummary?.subtotalAmount),
    discountAmount: formatDisplayAmount(amountSummary?.discountAmount),
    netAmount: formatDisplayAmount(amountSummary?.netAmount),
    taxAmount: formatDisplayAmount(amountSummary?.taxAmount),
    roundingAdjustment: formatDisplayAmount(amountSummary?.roundingAdjustment),
    totalAmount: formatDisplayAmount(amountSummary?.totalAmount),
  };
}

/**
 * Detail line／manualLines 金額物件。
 * 實測 BE 可能回 `amount` 或 `amount_summary` → dual-read：`amount ?? amountSummary`。
 * 不讀頂層扁平單價／小計當權威。
 */
export function getSalesOrderLineAmount(line) {
  return line?.amount ?? line?.amountSummary ?? null;
}

export function getSalesOrderLineUnitPrice(line) {
  return getSalesOrderLineAmount(line)?.unitPrice;
}

/** 畫面折數（價格倍率，0.9＝九折）；後端 `discount_rate` 是折抵比例，於此轉換。 */
export function getSalesOrderLineDiscountRate(line) {
  return apiDiscountRateToMultiplier(getSalesOrderLineAmount(line)?.discountRate);
}

/** 產品列小計：`amount`／`amountSummary` 的 `netAmount`。 */
export function getSalesOrderLineNetAmount(line) {
  return getSalesOrderLineAmount(line)?.netAmount ?? null;
}

export function getSalesOrderLineGrossAmount(line) {
  return getSalesOrderLineAmount(line)?.grossAmount ?? null;
}
